import React, { useMemo, useState, useEffect } from "react";
import { Box, Typography, Collapse } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CheckIcon from "@mui/icons-material/Check";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import LanguageIcon from "@mui/icons-material/Language";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import NearMeIcon from "@mui/icons-material/NearMe";
import { useMediaQuery } from "@mui/material";
import { useLocation } from "react-router-dom";
import api from "../../services/api";
import { userAPI } from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import { formatDisplayDate } from "../../utils/formatDisplayDate";
import { formatRecurringWeeklyDaysDisplay } from "../../utils/formatRecurringWeeklyDays";
import DealInfoSidebarSkeleton from "../skeletons/DealInfoSidebarSkeleton";
import { STATE_TO_AREAS } from "./dealList/constants/malaysiaLocations";
import CustomButton from "../ui/CustomButton";
import logger from "../../utils/logger";

// Brand location cache to avoid duplicate fetches
const brandLocationCache = {};

// Consistent spacing for sidebar sections
const SECTION = {
  py: 1.25,
  headerGap: 1.5,
  headerMb: 0.75,
  headerPx: 2.5,
  subtitlePx: 2.5,
  dividerMx: 2,
  dividerMy: 1.25,
};

const DEFAULT_REPORT_WHATSAPP_NUMBER = "60189426834";

/**
 * Human-readable available dates, or "Not specified" when validity is missing / empty.
 * not_applicable (N/A / empty upload) = no schedule info — show as not specified (listing still treats as always active).
 */
const getAvailableDatesDisplay = (validity) => {
  if (!validity || !validity.type) return "Not specified";

  switch (validity.type) {
    case "not_applicable":
      return "Not specified";

    case "range": {
      if (!validity.start || !validity.end) return "Not specified";
      const startDate = formatDisplayDate(validity.start);
      const endDate = formatDisplayDate(validity.end);
      return `${startDate} - ${endDate}`;
    }

    case "specific_dates": {
      if (!validity.specific_dates || !Array.isArray(validity.specific_dates)) {
        return "Not specified";
      }
      const parts = validity.specific_dates
        .filter((date) => date != null)
        .map((date) => formatDisplayDate(date))
        .filter((s) => s && String(s).trim() !== "");
      return parts.length > 0 ? parts.join(", ") : "Not specified";
    }

    case "recurring_weekly": {
      if (!validity.daysOfWeek || !Array.isArray(validity.daysOfWeek)) {
        return "Not specified";
      }
      const text = formatRecurringWeeklyDaysDisplay(validity.daysOfWeek);
      return text || "Not specified";
    }

    case "recurring_weekdays":
      return "Every Monday - Friday";

    case "recurring_weekends":
      return "Every Saturday - Sunday";

    case "recurring_monthly": {
      if (
        !validity.daysOfMonth ||
        !Array.isArray(validity.daysOfMonth) ||
        validity.daysOfMonth.length === 0
      ) {
        return "Not specified";
      }
      return `Monthly on day ${validity.daysOfMonth.join(", ")}`;
    }

    default:
      return "Not specified";
  }
};

const DealInfoSidebar = ({
  restaurant,
  deal,
  copyFeedback,
  handleCopyAddress,
  loading = false,
  reportWhatsAppNumber,
}) => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const [brandLocations, setBrandLocations] = useState(null);
  const [isBrandNationwide, setIsBrandNationwide] = useState(false);
  const [expandedArea, setExpandedArea] = useState(null);
  const [savedFilterArea, setSavedFilterArea] = useState("");

  // Map area to state
  const getStateForArea = (area) => {
    if (!area) return null;
    for (const [state, areas] of Object.entries(STATE_TO_AREAS)) {
      if (areas.includes(area)) {
        return state;
      }
    }
    return null;
  };

  // Get user's selected area with fallback priority:
  // 1. URL params (when coming from filtered deals list)
  // 2. Saved filter preferences (when coming from restaurant page or direct link)
  // 3. Empty (show all locations)
  const selectedArea = useMemo(() => {
    const urlParams = new URLSearchParams(location.search);
    const urlArea = urlParams.get("area") || "";

    // Priority 1: URL params (highest priority)
    if (urlArea) {
      return urlArea;
    }

    // Priority 2: Saved filter preferences (fallback)
    if (savedFilterArea) {
      return savedFilterArea;
    }

    // Priority 3: No filter (show all)
    return "";
  }, [location.search, savedFilterArea]);

  const selectedState = useMemo(() => {
    return getStateForArea(selectedArea);
  }, [selectedArea]);

  // Load user's saved filter preferences as fallback
  useEffect(() => {
    const loadSavedPreferences = async () => {
      // Only load if no URL area param and user is authenticated
      const urlParams = new URLSearchParams(location.search);
      const urlArea = urlParams.get("area");

      if (urlArea || !isAuthenticated) {
        return; // URL param takes priority, or user not authenticated
      }

      try {
        const response = await userAPI.getFilterPreferences();
        const savedFilters = response.data?.filterPreferences;
        if (savedFilters?.area) {
          setSavedFilterArea(savedFilters.area);
        }
      } catch (error) {
        // Silently fail - not critical if we can't load preferences
        logger.error("Failed to load filter preferences:", error);
      }
    };

    loadSavedPreferences();
  }, [isAuthenticated, location.search]);

  // Fetch brand locations if deal doesn't have specific locations
  useEffect(() => {
    const fetchBrandLocations = async () => {
      // Skip if deal has specific outlets — no need to fetch brand
      if (deal?.locations && deal.locations.length > 0) return;

      const brandId = deal?.brand?._id;
      if (!brandId) return;

      // Always fetch from API to get latest brand data (especially for nationwide flag)
      // Cache might be stale if brand was recently updated to nationwide
      try {
        const response = await api.get(`/brands/${brandId}`);
        const brandData = response.data;

        // Check if brand is nationwide (this takes highest priority)
        if (brandData.nationwide === true) {
          setIsBrandNationwide(true);
          setBrandLocations([]);
          // Cache the nationwide flag (clear any old location data)
          brandLocationCache[brandId] = { nationwide: true, locations: [] };
          return;
        }

        // Brand is not nationwide, parse outlet locations
        setIsBrandNationwide(false);
        const locations = brandData.outletLocations || [];

        // Convert nested brand structure to flat structure for display
        // Brand format: [{ state: "Selangor", areas: [{ area: "PJ", locations: ["loc1"] }] }]
        // Display format: [{ state: "Selangor", area: "PJ", label: "loc1" }]
        // Include state for filtering by user's selected state
        const flatLocations = [];
        locations.forEach((stateData) => {
          const state = stateData.state;
          if (stateData.areas && Array.isArray(stateData.areas)) {
            stateData.areas.forEach((areaData) => {
              if (areaData.locations && Array.isArray(areaData.locations)) {
                areaData.locations.forEach((loc) => {
                  flatLocations.push({
                    state: state,
                    area: areaData.area,
                    label: loc,
                  });
                });
              }
            });
          }
        });

        // Cache the locations
        brandLocationCache[brandId] = {
          nationwide: false,
          locations: flatLocations,
        };
        setBrandLocations(flatLocations);
      } catch (error) {
        logger.error("Error fetching brand locations:", error);
        setIsBrandNationwide(false);
        setBrandLocations([]);
      }
    };

    fetchBrandLocations();
  }, [deal?.brand?._id, deal?.locations]);

  // Determine which locations to display: deal-specific or brand-wide
  const displayLocations = useMemo(() => {
    // Priority 1: If brand is nationwide, always return empty array (will show "Nationwide")
    // This takes precedence over deal-specific locations
    if (isBrandNationwide) {
      return [];
    }

    // Priority 2: Use deal-specific locations if they exist
    if (deal?.locations && deal.locations.length > 0) {
      return deal.locations;
    }

    // Priority 3: Use brand outlet locations, filtered by selected state if applicable
    let locations = brandLocations || [];

    // If user has selected an area, filter locations to show only locations from the same state
    if (selectedState && locations.length > 0) {
      // Filter locations to only show those from the selected state
      // Brand locations have format: [{ state: "Selangor", area: "PJ", label: "loc1" }]
      locations = locations.filter((loc) => {
        return loc.state === selectedState;
      });
    }

    return locations;
  }, [deal?.locations, brandLocations, isBrandNationwide, selectedState]);

  // Check if we should show "Nationwide" — only when brand is explicitly nationwide
  const shouldShowNationwide = useMemo(() => {
    return isBrandNationwide;
  }, [isBrandNationwide]);

  // Group locations by area
  const locationsByArea = useMemo(() => {
    if (!displayLocations || displayLocations.length === 0) return {};
    const grouped = {};
    displayLocations.forEach((loc) => {
      const area = loc.area || "Other";
      if (!grouped[area]) {
        grouped[area] = [];
      }
      grouped[area].push(loc);
    });
    return grouped;
  }, [displayLocations]);

  const showNationwideAsText = useMemo(() => {
    return shouldShowNationwide;
  }, [shouldShowNationwide]);

  // Text to show for nationwide label
  const nationwideDisplayText = useMemo(() => {
    if (!showNationwideAsText) return "";
    return "All outlets";
  }, [showNationwideAsText]);

  return (
    <Box
      sx={{
        mt: isMobile ? 2 : 8,
        pb: isMobile ? 2 : 0,
      }}
    >
      {loading ? (
        <DealInfoSidebarSkeleton />
      ) : (
        /* Restaurant Information for all types */
        <Box sx={{ pb: "6rem" }}>
          {/* Last Updated */}
          {(deal?.updatedAt || deal?.createdAt) && (
            <Box sx={{ py: SECTION.py }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: SECTION.headerGap,
                  mb: SECTION.headerMb,
                  px: SECTION.headerPx,
                }}
              >
                <ArrowUpwardIcon sx={{ color: "#000", fontSize: 20 }} />
                <Typography
                  sx={{
                    fontSize: "16px",
                    fontWeight: 500,
                    color: "#000",
                  }}
                >
                  Last Updated
                </Typography>
              </Box>

              <Typography
                sx={{
                  lineHeight: 1.5,
                  px: SECTION.subtitlePx,
                  color: "#666",
                }}
              >
                {formatDisplayDate(deal.updatedAt || deal.createdAt)}
              </Typography>
            </Box>
          )}

          {(deal?.createdAt || deal?.updatedAt) && (
            <Box
              sx={{
                borderBottom: "1px solid #e0e0e0",
                mx: SECTION.dividerMx,
                my: SECTION.dividerMy,
              }}
            />
          )}

          {/* Deal Available Dates — show "Not specified" when validity missing or empty */}
          {deal && (
            <Box sx={{ py: SECTION.py }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: SECTION.headerGap,
                  mb: SECTION.headerMb,
                  px: SECTION.headerPx,
                }}
              >
                <CalendarTodayIcon sx={{ color: "#000", fontSize: 20 }} />
                <Typography
                  sx={{
                    fontSize: "16px",
                    fontWeight: 500,
                    color: "#000",
                  }}
                >
                  Available Dates
                </Typography>
              </Box>

              <Typography
                sx={{
                  lineHeight: 1.5,
                  whiteSpace: "pre-line",
                  color: "#666",
                  px: SECTION.subtitlePx,
                }}
              >
                {getAvailableDatesDisplay(deal.validity)}
              </Typography>
            </Box>
          )}

          {deal && (
            <Box
              sx={{
                borderBottom: "1px solid #e0e0e0",
                mx: SECTION.dividerMx,
                my: SECTION.dividerMy,
              }}
            />
          )}

          {/* Available Time */}
          <Box sx={{ py: SECTION.py }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: SECTION.headerGap,
                mb: SECTION.headerMb,
                px: SECTION.headerPx,
              }}
            >
              <AccessTimeIcon sx={{ color: "#000", fontSize: 20 }} />
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: 500,
                  color: "#000",
                }}
              >
                Available Time
              </Typography>
            </Box>
            <Typography
              sx={{
                lineHeight: 1.5,
                whiteSpace: "pre-line",
                px: SECTION.subtitlePx,
                color: "#666",
              }}
            >
              {deal?.availableTime && deal.availableTime.trim() !== ""
                ? deal.availableTime
                : "Not specified"}
            </Typography>
          </Box>

          <Box
            sx={{
              borderBottom: "1px solid #e0e0e0",
              mx: SECTION.dividerMx,
              my: SECTION.dividerMy,
            }}
          />

          {/* Deal Available Locations - Grouped by Area */}
          <Box sx={{ py: SECTION.py }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: SECTION.headerGap,
                mb: SECTION.headerMb,
                px: SECTION.headerPx,
              }}
            >
              <LocationOnIcon sx={{ color: "#000", fontSize: 20 }} />
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: 500,
                  color: "#000",
                }}
              >
                Available Locations
              </Typography>
            </Box>

            <Box sx={{ px: SECTION.subtitlePx }}>
              {showNationwideAsText ? (
                <Typography sx={{ lineHeight: 1.5, color: "#666" }}>
                  {nationwideDisplayText}
                </Typography>
              ) : Object.keys(locationsByArea).length > 0 ? (
                (() => {
                  const areaKeys = Object.keys(locationsByArea);
                  // If the only group is "Nationwide" (unresolved outlets), skip the
                  // accordion wrapper and show the outlets as a flat list directly.
                  const isSingleNationwideGroup =
                    areaKeys.length === 1 &&
                    areaKeys[0].toLowerCase() === "nationwide";

                  if (isSingleNationwideGroup) {
                    return (
                      <Box component="ul" sx={{ pl: 0, my: 0, listStyle: "none" }}>
                        {locationsByArea[areaKeys[0]].map((loc, idx) => (
                          <Box
                            key={idx}
                            component="li"
                            sx={{ listStyleType: "none", pl: 0, mb: 0.5 }}
                          >
                            <Typography
                              sx={{
                                fontSize: "16px",
                                lineHeight: 1.5,
                                color: "#666",
                              }}
                            >
                              {loc.label}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    );
                  }

                  return (
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                    >
                      {areaKeys.map((area) => (
                        <Box key={area}>
                          <Box
                            onClick={() =>
                              setExpandedArea(expandedArea === area ? null : area)
                            }
                            sx={{
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              px: 2,
                              py: isMobile ? 1.5 : 1,
                              bgcolor: "#f9f9f9",
                              borderRadius: "8px",
                              ...(isMobile
                                ? {
                                    "&:active": {
                                      bgcolor: "#f0f0f0",
                                    },
                                  }
                                : {
                                    "&:hover": {
                                      bgcolor: "#f0f0f0",
                                    },
                                  }),
                            }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 600,
                                fontSize: isMobile ? "16px" : "14px",
                              }}
                            >
                              {area} ({locationsByArea[area].length})
                            </Typography>
                            <ExpandMoreIcon
                              sx={{
                                fontSize: isMobile ? "20px" : "18px",
                                transform:
                                  expandedArea === area
                                    ? "rotate(180deg)"
                                    : "rotate(0deg)",
                                transition: "transform 0.2s ease",
                              }}
                            />
                          </Box>

                          <Collapse in={expandedArea === area} timeout="auto">
                            <Box sx={{ mt: 1, ml: 2 }}>
                              <Box component="ul" sx={{ pl: 0, my: 0, listStyle: "none" }}>
                                {locationsByArea[area].map((loc, idx) => (
                                  <Box
                                    key={idx}
                                    component="li"
                                    sx={{
                                      listStyleType: "none",
                                      pl: 0,
                                      mb: 0.5,
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: "16px",
                                        lineHeight: 1.5,
                                        color: "#666",
                                      }}
                                    >
                                      {loc.label}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          </Collapse>
                        </Box>
                      ))}
                    </Box>
                  );
                })()
              ) : (
                <Typography
                  sx={{
                    lineHeight: 1.5,
                    color: "#666",
                  }}
                >
                  Location not specified
                </Typography>
              )}

              {/* Exclusions note */}
              {deal?.exclusions && (
                <Typography
                  sx={{
                    fontSize: "13px",
                    lineHeight: 1.5,
                    color: "#888",
                    mt: 1.5,
                    fontStyle: "italic",
                  }}
                >
                  ⚠️ {deal.exclusions}
                </Typography>
              )}
            </Box>
          </Box>

          <Box
            sx={{
              borderBottom: "1px solid #e0e0e0",
              mx: SECTION.dividerMx,
              my: SECTION.dividerMy,
            }}
          />

          {/* Find nearest outlet - opens Google Maps */}
          {restaurant?.name && (
            <Box
              component="a"
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                `${restaurant.name} near me`,
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: "block",
                color: "inherit",
                textDecoration: "none",
                "&:hover": {
                  backgroundColor: "#f0f0f0",
                },
              }}
            >
              <Box sx={{ py: SECTION.py }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: SECTION.headerGap,
                    mb: SECTION.headerMb,
                    px: SECTION.headerPx,
                  }}
                >
                  <NearMeIcon sx={{ color: "#000", fontSize: 20 }} />
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontWeight: 500,
                      color: "#000",
                    }}
                  >
                    Find Nearest Outlet
                  </Typography>
                  <OpenInNewIcon
                    sx={{ color: "#666", fontSize: 16, ml: "auto" }}
                  />
                </Box>
                <Typography
                  sx={{
                    lineHeight: 1.5,
                    px: SECTION.subtitlePx,
                    color: "#666",
                  }}
                >
                  Open in Google Maps
                </Typography>
              </Box>
            </Box>
          )}

          {restaurant?.name && (
            <Box
              sx={{
                borderBottom: "1px solid #e0e0e0",
                mx: SECTION.dividerMx,
                my: SECTION.dividerMy,
              }}
            />
          )}

          {/* Deal Source Link */}
          {(() => {
            const rawSource = deal?.source;
            const sourceUrl =
              (typeof rawSource === "string" ? rawSource : rawSource?.url) ||
              deal?.sourceUrl ||
              deal?.source_url ||
              null;

            return (
              <Box
                component={sourceUrl ? "a" : "div"}
                href={sourceUrl || undefined}
                target={sourceUrl ? "_blank" : undefined}
                rel={sourceUrl ? "noopener noreferrer" : undefined}
                sx={{
                  py: SECTION.py,
                  px: SECTION.headerPx,
                  color: "inherit",
                  textDecoration: "none",
                  transition: "all 0.2s ease",
                  position: "relative",
                  display: "block",
                  ...(sourceUrl && {
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "#f0f0f0",
                    },
                  }),
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: SECTION.headerGap,
                    mb: SECTION.headerMb,
                  }}
                >
                  <LanguageIcon sx={{ color: "#000", fontSize: 20 }} />
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontWeight: 500,
                      color: "#000",
                    }}
                  >
                    Deal Source
                  </Typography>
                  {sourceUrl && (
                    <OpenInNewIcon
                      sx={{ color: "#666", fontSize: 16, ml: "auto" }}
                    />
                  )}
                </Box>
                <Typography
                  sx={{
                    lineHeight: 1.3,
                    color: sourceUrl ? "#1976d2" : "#666",
                    wordBreak: "break-all",
                  }}
                >
                  {sourceUrl || "Not available"}
                </Typography>
              </Box>
            );
          })()}

          {/* Extra breathing room after Deal Source */}
          <Box
            sx={{
              borderBottom: "1px solid #e0e0e0",
              mx: SECTION.dividerMx,
              mt: SECTION.dividerMy,
              mb: 3,
            }}
          />

          {/* Address */}
          {restaurant?.address && (
            <Box
              onClick={handleCopyAddress}
              sx={{
                py: SECTION.py,
                px: SECTION.headerPx,
                cursor: "pointer",
                transition: "all 0.2s ease",
                position: "relative",
                "&:hover": {
                  backgroundColor: "#f0f0f0",
                  "& .copy-icon": {
                    opacity: 1,
                  },
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: SECTION.headerGap,
                  mb: SECTION.headerMb,
                }}
              >
                <LocationOnIcon sx={{ color: "#000", fontSize: 20 }} />
                <Typography
                  sx={{
                    fontSize: "16px",
                    fontWeight: 500,
                    color: "#000",
                  }}
                >
                  Address
                </Typography>
              </Box>
              <Typography
                sx={{
                  lineHeight: 1.3,
                }}
              >
                {restaurant.address}
              </Typography>
              {copyFeedback ? (
                <CheckIcon
                  sx={{
                    color: "#4caf50",
                    fontSize: 16,
                    position: "absolute",
                    top: "2rem",
                    right: "1rem",
                  }}
                />
              ) : (
                <ContentCopyIcon
                  className="copy-icon"
                  sx={{
                    color: "#666",
                    fontSize: 16,
                    opacity: 0,
                    transition: "opacity 0.2s ease",
                    position: "absolute",
                    top: "2rem",
                    right: "1rem",
                  }}
                />
              )}
            </Box>
          )}

          {restaurant?.address && (
            <Box
              sx={{
                borderBottom: "1px solid #e0e0e0",
                mx: SECTION.dividerMx,
                my: SECTION.dividerMy,
              }}
            />
          )}

          {/* Opening Hours */}
          {restaurant?.openingTime && (
            <Box
              sx={{
                py: SECTION.py,
                px: SECTION.headerPx,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: SECTION.headerGap,
                  mb: SECTION.headerMb,
                }}
              >
                <AccessTimeIcon sx={{ color: "#000", fontSize: 20 }} />
                <Typography
                  sx={{
                    fontSize: "16px",
                    fontWeight: 500,
                    color: "#000",
                  }}
                >
                  Opening Hours
                </Typography>
              </Box>
              <Typography
                sx={{
                  lineHeight: 1.5,
                  whiteSpace: "pre-line",
                  color: "#666",
                }}
              >
                {restaurant.openingTime}
              </Typography>
            </Box>
          )}

          {restaurant?.openingTime && (
            <Box
              sx={{
                borderBottom: "1px solid #e0e0e0",
                mx: SECTION.dividerMx,
                my: SECTION.dividerMy,
              }}
            />
          )}

          {/* Report Deal — red button, links to WhatsApp */}
          <Box
            sx={{
              pt: 2,
              pb: 1,
              px: SECTION.headerPx,
            }}
          >
            <CustomButton
              variant="secondary"
              onClick={() => {
                const number =
                  reportWhatsAppNumber || DEFAULT_REPORT_WHATSAPP_NUMBER;
                const dealUrl = deal?.shortId
                  ? `${typeof window !== "undefined" ? window.location.origin : ""}/deal/${deal.shortId}`
                  : typeof window !== "undefined"
                    ? window.location.href
                    : "";
                const message = `Hi, I'd like to report an inaccuracy in a deal.\n\nDeal ID: ${deal?.shortId || deal?._id || "—"}\nDeal: ${deal?.title || "Unknown"}\nLink: ${dealUrl}`;
                window.open(
                  `https://wa.me/${number}?text=${encodeURIComponent(message)}`,
                  "_blank",
                  "noopener,noreferrer",
                );
              }}
              sx={{
                borderRadius: "20px",
                paddingY: 1,
                paddingX: 2.5,
                display: "flex",
                alignItems: "center",
                gap: 1,
                backgroundColor: "#fee2e2",
                color: "#dc2626",
                "&:hover": {
                  backgroundColor: "#fecaca",
                },
              }}
            >
              <WhatsAppIcon sx={{ fontSize: 20 }} />
              Report Deal
            </CustomButton>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default DealInfoSidebar;
